import { Component } from 'solid-js';
import { Icon } from '@tcweb/connect-ui';

export type MainToolbarButtonSize = 'small' | 'medium';

export interface MainToolbarButtonProps {
  readonly iconName: string;
  readonly ariaLabel: string;
  readonly tooltip?: string;
  readonly text?: string;
  readonly size?: MainToolbarButtonSize;
  readonly pressed?: boolean;
  readonly disabled?: boolean;
  readonly testId?: string;
  action(): void;
}

const sizeClass = (size: MainToolbarButtonSize): string =>
  size === 'medium' ? 'h-10 w-10' : 'h-8 w-8';

/**
 * Plain icon button in the "main toolbar" visual style — theme-driven
 * `text-base-content` fill with `bg-gray-0` hover / pressed treatment.
 * Extracted so custom entries (currently just the visibility dropdown
 * trigger) can adopt the same look as built-in entries without going
 * through `BasicButton`, which paints icons in the tertiary hollow
 * style and reads visibly lighter next to its neighbours.
 */
export const MainToolbarButton: Component<MainToolbarButtonProps> = (props) => {
  const size = (): MainToolbarButtonSize => props.size ?? 'medium';
  return (
    <button
      type="button"
      data-testid={props.testId}
      data-size={size()}
      class={`${sizeClass(size())} flex items-center justify-center rounded-sm border-0 p-0 text-base-content transition-colors duration-300 ${
        props.pressed ? 'bg-gray-0' : ''
      } ${
        props.disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:bg-gray-0'
      }`}
      title={props.tooltip ?? props.ariaLabel}
      aria-label={props.ariaLabel}
      aria-pressed={props.pressed}
      disabled={props.disabled}
      onClick={() => props.action()}
    >
      <span aria-hidden="true">
        <Icon
          icon={props.iconName}
          size={size() === 'medium' ? 'icon-medium' : 'icon-small'}
        />
      </span>
      <span class="sr-only">{props.text ?? props.ariaLabel}</span>
    </button>
  );
};
